/**
 * Authentication Guard
 *
 * Functional route guard that prevents unauthorized users from accessing protected routes.
 * If the user is not authenticated, they are redirected to the login page with a returnUrl
 * query parameter to redirect them back after successful login.
 *
 * This guard follows the Angular 15+ functional guard pattern.
 *
 * @example
 * ```typescript
 * // In app.routes.ts
 * export const routes: Routes = [
 *   {
 *     path: 'dashboard',
 *     canActivate: [authGuard],
 *     component: DashboardComponent
 *   },
 *   {
 *     path: '',
 *     canActivate: [authGuard],
 *     children: [
 *       { path: 'schools', component: SchoolsComponent },
 *       { path: 'reports', component: ReportsComponent }
 *     ]
 *   }
 * ];
 * ```
 */

import { inject } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { CanActivateFn } from '@angular/router';
import { AuthService } from '../services/auth.service';

/**
 * Guard function that checks if the user is authenticated
 *
 * If authenticated, allows navigation to the requested route.
 * If not authenticated, redirects to the login page and stores the
 * attempted URL as a returnUrl query parameter.
 *
 * @param route - The activated route snapshot
 * @param state - The router state snapshot
 * @returns True if navigation is allowed, UrlTree for redirect if not
 *
 * @example
 * When an unauthenticated user tries to access /dashboard:
 * 1. authGuard checks authentication
 * 2. User is not authenticated
 * 3. User is redirected to /auth/login?returnUrl=/dashboard
 * 4. After successful login, user can be redirected back to /dashboard
 */
export const authGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): boolean | UrlTree => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Check if user is authenticated
  if (authService.isAuthenticated()) {
    return true;
  }

  // User is not authenticated - redirect to login with returnUrl
  return router.createUrlTree(['/auth/login'], {
    queryParams: {
      returnUrl: state.url
    }
  });
};
